import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../api/client';
import PageTemplate from './PageTemplate';
import './ModulePage.css';

function normalizeCoursesResponse(data) {
  const list = Array.isArray(data)
    ? data
    : data?.courses
    ? Array.isArray(data.courses)
      ? data.courses
      : data.courses.results || []
    : data?.results || [];

  if (!Array.isArray(list)) {
    console.warn('Unexpected courses response format:', data);
    return [];
  }

  return list.map((c, idx) => {
    const id = c?.course_id ?? c?.id ?? idx;
    const name = c?.course_name ?? c?.name ?? '';
    const feeRaw = c?.course_fee ?? c?.fee ?? 0;
    const fee = typeof feeRaw === 'number' ? feeRaw : Number(feeRaw);
    const createdAt = c?.created_at ?? '';
    const updatedAt = c?.updated_at ?? '';
    const duration = c?.duration != null ? String(c.duration) : '';
    const level = c?.level != null ? String(c.level) : '';

    return {
      id,
      name: String(name),
      fee: Number.isFinite(fee) ? fee : 0,
      createdAt,
      updatedAt,
      duration,
      level,
    };
  });
}

function Courses() {
  const [courses, setCourses] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const requestIdRef = useRef(0);
  const navigate = useNavigate();

  const loadCourses = useCallback(
    async ({ isRefresh = false } = {}) => {
      const requestId = Date.now();
      requestIdRef.current = requestId;

      if (!isRefresh) {
        setLoading(true);
        setCourses([]);
      }
      setError('');

      try {
        const res = await apiClient.get('/api/courses/');
        if (requestIdRef.current !== requestId) return;
        const next = normalizeCoursesResponse(res.data);
        setCourses(next);
      } catch (e) {
        if (requestIdRef.current !== requestId) return;

        if (e?.response?.status === 401) {
          navigate('/login', { replace: true });
          return;
        }

        const errorMsg =
          e?.response?.data?.detail ||
          e?.response?.data?.message ||
          e?.message ||
          'Unable to load courses. Please try again.';
        setError(errorMsg);
      } finally {
        if (requestIdRef.current !== requestId) return;
        setLoading(false);
      }
    },
    [navigate]
  );

  useEffect(() => {
    loadCourses();
  }, [loadCourses]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      loadCourses({ isRefresh: true });
    }, 60000);
    return () => window.clearInterval(intervalId);
  }, [loadCourses]);

  const filteredCourses = useMemo(() => {
    const term = search.toLowerCase().trim();
    if (!term) return courses;
    return courses.filter((course) => {
      return (
        course.name.toLowerCase().includes(term) ||
        course.duration.toLowerCase().includes(term) ||
        course.level.toLowerCase().includes(term)
      );
    });
  }, [courses, search]);

  const totalCourses = courses.length;
  const totalFee = courses.reduce((sum, course) => sum + course.fee, 0);
  const avgFee = totalCourses ? Math.round(totalFee / totalCourses) : 0;

  const handleEdit = (courseId) => {
    navigate(`/courses/edit/${courseId}`);
  };

  const handleDelete = async (courseId, courseName) => {
    if (
      !window.confirm(
        `Are you sure you want to delete "${courseName}"? This action cannot be undone.`
      )
    ) {
      return;
    }

    setDeletingId(courseId);
    try {
      await apiClient.delete(`/api/courses/${courseId}/`);
      setCourses((prev) => prev.filter((c) => c.id !== courseId));
      setError('');
    } catch (err) {
      console.error('Error deleting course:', err);

      const errorMsg =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        'Unable to delete course. Please try again.';
      setError(errorMsg);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <PageTemplate
      title="Course Management"
      subtitle="Create, edit, and manage course offerings"
    >
      <div className="teachers-page">
        <div className="course-stats">
          <div className="course-stat-card">
            <span className="course-stat-label">Total Courses</span>
            <span className="course-stat-value">{totalCourses}</span>
          </div>
          <div className="course-stat-card">
            <span className="course-stat-label">Total Fee Value</span>
            <span className="course-stat-value">
              ₹ {totalFee.toLocaleString()}
            </span>
          </div>
          <div className="course-stat-card">
            <span className="course-stat-label">Average Fee</span>
            <span className="course-stat-value">
              ₹ {avgFee.toLocaleString()}
            </span>
          </div>
        </div>

        <div className="module-actions">
          <input
            type="search"
            placeholder="Search by course name, level, duration…"
            className="search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button
            type="button"
            className="btn-primary"
            onClick={() => navigate('/courses/add')}
          >
            Add Course
          </button>
        </div>

        {error ? (
          <p className="form-error" style={{ marginTop: '12px' }}>
            {error}
          </p>
        ) : null}

        <div
          className="teachers-table-wrapper courses-table-wrapper"
          style={{ marginTop: '24px' }}
          role="region"
          aria-label="Courses table"
        >
          <table className="teachers-table courses-table">
            <colgroup>
              <col className="courses-col courses-col--id" />
              <col className="courses-col courses-col--name" />
              <col className="courses-col courses-col--fee" />
              <col className="courses-col courses-col--duration" />
              <col className="courses-col courses-col--level" />
              <col className="courses-col courses-col--actions" />
            </colgroup>
            <thead>
              <tr>
                <th className="courses-col courses-col--id">Course ID</th>
                <th className="courses-col courses-col--name">Course Name</th>
                <th className="courses-col courses-col--fee">Course Fee</th>
                <th className="courses-col courses-col--duration">Duration</th>
                <th className="courses-col courses-col--level">Level</th>
                <th className="courses-col courses-col--actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" className="teachers-empty teachers-empty--loading">
                    Loading courses…
                  </td>
                </tr>
              ) : filteredCourses.length === 0 ? (
                <tr>
                  <td colSpan="6" className="teachers-empty">
                    No courses found. Add a new course above.
                  </td>
                </tr>
              ) : (
                filteredCourses.map((course) => (
                  <tr key={course.id}>
                    <td
                      className="courses-col courses-col--id table-cell-muted course-id-cell"
                      title={String(course.id)}
                    >
                      {String(course.id).length > 12
                        ? `${String(course.id).slice(0, 8)}…`
                        : course.id}
                    </td>
                    <td className="courses-col courses-col--name">
                      <span className="table-cell-strong course-name-cell" title={course.name}>
                        {course.name}
                      </span>
                    </td>
                    <td className="courses-col courses-col--fee fee-cell">
                      ₹ {course.fee.toLocaleString()}
                    </td>
                    <td className="courses-col courses-col--duration">
                      {course.duration ? (
                        <span className="course-meta-pill course-meta-pill--subtle">{course.duration}</span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="courses-col courses-col--level">
                      {course.level ? (
                        <span className="course-meta-pill course-meta-pill--level">{course.level}</span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="courses-col courses-col--actions">
                      <div className="table-actions">
                        <button
                          onClick={() => handleEdit(course.id)}
                          type="button"
                          className="table-btn table-btn--primary"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(course.id, course.name)}
                          type="button"
                          disabled={deletingId === course.id}
                          className="table-btn table-btn--danger"
                        >
                          {deletingId === course.id ? 'Deleting...' : 'Delete'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </PageTemplate>
  );
}

export default Courses;
